import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore'
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { timestampToIso } from './customersService'
import { uploadImages, validateImage } from './imageService'
import type { Enquiry, EnquiryInput } from '../types/admin'

export const enquiriesRef = collection(db, 'enquiries')

function toEnquiry(snapshot: QueryDocumentSnapshot<DocumentData>): Enquiry {
  const data = snapshot.data()
  return {
    id: snapshot.id,
    name: data.name ?? '',
    phone: data.phone ?? '',
    email: data.email ?? '',
    address: data.address ?? '',
    description: data.description ?? '',
    status: data.status ?? 'Ny henvendelse',
    images: Array.isArray(data.images) ? data.images : [],
    createdAt: timestampToIso(data.createdAt),
    caseId: data.caseId ?? undefined,
  }
}

export function subscribeEnquiries(
  onChange: (enquiries: Enquiry[]) => void,
  onError: (error: unknown) => void,
): () => void {
  return onSnapshot(
    query(enquiriesRef, orderBy('createdAt', 'desc')),
    (snapshot) => onChange(snapshot.docs.map(toEnquiry)),
    onError,
  )
}

/**
 * Sikker indsendelse:
 * 1) opret dokumentet i enquiries
 * 2) upload billeder til enquiries/{id}/...
 * 3) gem Storage-stier og -URL'er på dokumentet
 */
export async function submitEnquiry(input: EnquiryInput): Promise<string> {
  for (const file of input.files) {
    const problem = validateImage(file)
    if (problem) throw new Error(problem)
  }

  const created = await addDoc(enquiriesRef, {
    name: input.name.trim(),
    phone: input.phone.trim(),
    email: input.email.trim(),
    address: input.address.trim(),
    description: input.description.trim(),
    status: 'Ny henvendelse',
    images: [],
    imagePaths: [],
    createdAt: serverTimestamp(),
  })

  if (input.files.length > 0) {
    const uploaded = await uploadImages(input.files, `enquiries/${created.id}`)
    await updateDoc(doc(db, 'enquiries', created.id), {
      images: uploaded,
      imagePaths: uploaded.map((image) => image.path),
    })
  }

  return created.id
}

export async function updateEnquiryDoc(id: string, patch: Partial<Enquiry>): Promise<void> {
  await updateDoc(doc(db, 'enquiries', id), { ...patch, updatedAt: serverTimestamp() })
}
